import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { userApi } from '../../services/api';
import { changePasswordSchema } from '../../utils/validationSchemas';
import LoadingSpinner from '../LoadingSpinner';
import './ProfileForm.css';

const ChangePasswordForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(changePasswordSchema),
    mode: 'onBlur'
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await userApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      reset();
      setShowPasswords({ current: false, new: false, confirm: false });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to change password'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-form-container">
      <div className="profile-header">
        <h2>Change password</h2>
        <p>Update your account password</p>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="profile-form">
        <div className="form-group">
          <label htmlFor="currentPassword">Current password *</label>
          <div className="password-input-container">
            <input
              id="currentPassword"
              type={showPasswords.current ? 'text' : 'password'}
              {...register('currentPassword')}
              className={errors.currentPassword ? 'input-error' : ''}
              placeholder="Enter your current password"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => togglePasswordVisibility('current')}
            >
              {showPasswords.current ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {errors.currentPassword && (
            <span className="field-error">{errors.currentPassword.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="newPassword">New password *</label>
          <div className="password-input-container">
            <input
              id="newPassword"
              type={showPasswords.new ? 'text' : 'password'}
              {...register('newPassword')}
              className={errors.newPassword ? 'input-error' : ''}
              placeholder="Enter your new password (min. 6 characters)"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => togglePasswordVisibility('new')}
            >
              {showPasswords.new ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {errors.newPassword && (
            <span className="field-error">{errors.newPassword.message}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm new password *</label>
          <div className="password-input-container">
            <input
              id="confirmPassword"
              type={showPasswords.confirm ? 'text' : 'password'}
              {...register('confirmPassword')}
              className={errors.confirmPassword ? 'input-error' : ''}
              placeholder="Confirm your new password"
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => togglePasswordVisibility('confirm')}
            >
              {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {errors.confirmPassword && (
            <span className="field-error">{errors.confirmPassword.message}</span>
          )}
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="save-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="small" />
                Changing password...
              </>
            ) : (
              'Change password'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordForm;
